<template>
  <div
    ref="containerRef"
    class="product__testimonials__item flex flex-col relative overflow-visible rounded-box font-secondary p-4"
    :class="{ 'py-6': !isRatingFirstLayout }"
    :style="{ background: color }"
  >
    <div
      class="flex flex-wrap text-dark-100"
      :class="{ 'flex-col': isRatingFirstLayout }"
    >
      <Stars
        :score="rating"
        class="product__testimonials__stars -ml-1"
        :class="{ 'order-2': !isRatingFirstLayout }"
        :size="starSize"
      />
      <div
        class="product__testimonials__content"
        :class="{ 'order-1 mr-2': !isRatingFirstLayout }"
      >
        <div class="product__testimonials__details">
          <strong
            v-if="from"
            class="text-base font-bold"
          >
            {{ from }}
          </strong>
          <span
            v-if="date"
            class="text-base font-normal"
            data-i18n="wasOnTheTrip"
          >
            <strong
              v-if="from"
            >
              {{ ', ' }}
            </strong>
            <span v-if="!tripName">
              {{ toUnixNewlines($t('wasOnTheTrip')) }} {{ date }}
            </span>
            <component
              :is="tripPath ? NuxtLink : 'span'"
              v-else
              :to="tripPath ?? undefined"
              class="text-black"
              :class="{ 'underline': tripPath }"
            >
              {{ date }} - {{ tripName }}
            </component>
          </span>
        </div>
      </div>
    </div>
    <p
      v-if="hasText"
      class="product__testimonials__text text-dark-100 mb-0"
      :class="textClass"
    >
      {{ toUnixNewlines(text) }}
    </p>
    <slot />
    <div
      v-if="trianglePointerDirection === 'left'"
      class="chat-arrow-left top-0 absolute h-full flex items-center pt-4"
      :style="{ color }"
    >
      <svg
        class="hidden md:block"
        height="30"
        width="23"
        fill="currentColor"
      >
        <polygon points="23 0, 0 15, 23 30" />
      </svg>
      <svg
        class="block md:hidden"
        height="30"
        width="14"
        fill="currentColor"
      >
        <polygon points="14 0, 0 15, 14 30" />
      </svg>
    </div>
    <div
      v-if="trianglePointerDirection === 'right'"
      class="chat-arrow-right top-0 absolute h-full flex items-center pt-4"
      :style="{ color }"
    >
      <svg
        class="hidden md:block"
        height="30"
        width="23"
        fill="currentColor"
      >
        <polygon points="0 0, 23 15, 0 30" />
      </svg>
      <svg
        class="block md:hidden"
        height="30"
        width="14"
        fill="currentColor"
      >
        <polygon points="0 0, 14 15, 0 30" />
      </svg>
    </div>
  </div>
</template>

<script setup>
import colors from '#tailwind-config/theme/colors'
import { NuxtLink } from '#components'

const props = defineProps({
  rating: {
    type: Number,
    required: true,
  },

  starSize: {
    type: String,
    default: 'lg',
    validator: value => ['sm', 'lg'].includes(value),
  },

  date: {
    type: String,
    default: null,
  },

  from: {
    type: String,
    default: '',
  },

  fromKey: {
    type: String,
    default: '',
  },

  tripName: {
    type: String,
    default: null,
  },

  tripPath: {
    type: String,
    default: null,
  },

  text: {
    type: String,
    default: '',
  },

  textClass: {
    type: [String, Object, Array],
    default: null,
  },

  /**
   * @prop {string} triangle left | right | auto
   * If auto: align to closest side
   */
  triangle: {
    type: String,
    default: null,
    validator: value => ['left', 'right', 'auto'].includes(value),
  },

  color: {
    type: String,
    default: colors.gray[300],
  },
})

const trianglePointerDirection = ref(null)
const containerRef = ref(null)

const hasText = computed(() => !!props.text?.trim())
const isRatingFirstLayout = computed(() => !!props.date)

watch(() => props.triangle, () => {
  getTrianglePointerDirection()
})

onMounted(() => {
  getTrianglePointerDirection()
})

const getTrianglePointerDirection = () => {
  if (props.triangle !== 'auto') {
    trianglePointerDirection.value = props.triangle
    return
  }

  nextTick(() => {
    if (containerRef.value) {
      const { offsetLeft } = containerRef.value

      if (offsetLeft === 0) {
        trianglePointerDirection.value = 'left'
      } else {
        trianglePointerDirection.value = 'right'
      }
    }
  })
}
</script>

<style lang="scss">
.product__testimonials {
  &__item {
    margin-bottom: 8px;

    &:last-of-type {
      margin-bottom: 0;
    }

    @media (max-width: theme('screens.sm')) {
      flex-direction: column;
    }

    &:hover {
      background: theme('colors.lightest-blue-transparent') !important;

      svg {
        color: theme('colors.lightest-blue-transparent') !important;
      }
    }

    .chat-arrow-left {
      color: inherit;
      right: 100%;
    }
    .chat-arrow-right {
      color: inherit;
      left: 100%;
    }
  }

  &__stars {
    clear: both;
    display: flex;
    padding-bottom: .5em;

    img {
      margin: 0 2px;
    }
  }

  &__details {
    margin-bottom: .5em;

    @media (max-width: theme('screens.sm')) {
      font-size: 14px;
    }
  }
}
</style>
